import { useState } from "react";
import { Lock, Users, CheckSquare, ListTodo, Clock, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SummaryCard({ testId, icon, label, value, color }) {
  const bgMap = {
    blue: "rgba(59, 130, 246, 0.08)",
    amber: "rgba(245, 158, 11, 0.08)",
    emerald: "rgba(16, 185, 129, 0.08)",
    violet: "rgba(139, 92, 246, 0.08)",
  };

  return (
    <div
      data-testid={testId}
      className="rounded-xl px-5 py-4"
      style={{ background: "rgba(30, 41, 59, 0.8)", border: "1px solid rgba(148, 163, 184, 0.1)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: bgMap[color] }}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-100">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function UserRow({ user, isLast }) {
  const formatDate = (isoString) => {
    if (!isoString) return "No activity";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <tr
      data-testid={`report-user-row-${user.email}`}
      className="transition-colors"
      style={{ borderBottom: isLast ? "none" : "1px solid rgba(148, 163, 184, 0.06)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(148, 163, 184, 0.05)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-slate-200">{user.name || "—"}</div>
        <div className="text-xs text-slate-500">{user.email}</div>
      </td>
      <td className="text-center px-4 py-4">
        <span className="text-sm font-semibold text-slate-200">{user.total_tasks}</span>
      </td>
      <td className="text-center px-4 py-4">
        <span className="text-sm text-emerald-400">{user.active_tasks}</span>
      </td>
      <td className="text-center px-4 py-4">
        <span className="text-sm text-slate-400">{user.completed_tasks}</span>
      </td>
      <td className="px-4 py-4 hidden sm:table-cell">
        <span className="text-sm text-slate-400">{formatDate(user.last_activity)}</span>
      </td>
      <td className="px-4 py-4 hidden sm:table-cell">
        <span className="text-sm text-slate-500">{formatDate(user.created_at)}</span>
      </td>
    </tr>
  );
}

function ReportLogin({ onAuthenticated }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/api/report`, false);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(JSON.stringify({ password }));

      if (xhr.status === 200) {
        onAuthenticated(JSON.parse(xhr.responseText));
      } else if (xhr.status === 403) {
        setError("Invalid password");
      } else {
        setError("Failed to load report");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="report-login-page"
      className="min-h-screen flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}
    >
      <div
        className="w-full max-w-sm mx-4 rounded-xl p-8"
        style={{ background: "rgba(30, 41, 59, 0.8)", border: "1px solid rgba(148, 163, 184, 0.15)" }}
      >
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(59, 130, 246, 0.15)" }}>
            <Lock className="w-6 h-6 text-blue-400" />
          </div>
        </div>
        <h1 data-testid="report-title" className="text-xl font-semibold text-center text-slate-100 mb-1">
          Admin Report
        </h1>
        <p className="text-sm text-center text-slate-400 mb-6">Enter password to view</p>

        <form onSubmit={handleSubmit}>
          <input
            data-testid="report-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            className="w-full px-4 py-3 rounded-lg text-sm text-slate-100 placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
            style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(148, 163, 184, 0.15)" }}
          />
          {error && (
            <p data-testid="report-error" className="text-red-400 text-sm mt-2">{error}</p>
          )}
          <button
            data-testid="report-submit-btn"
            type="submit"
            disabled={loading || !password}
            className="w-full mt-4 py-3 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: "#3b82f6" }}
          >
            {loading ? "Loading..." : "View Report"}
          </button>
        </form>

        <button
          data-testid="report-back-btn"
          onClick={() => navigate("/")}
          className="w-full mt-3 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to app
        </button>
      </div>
    </div>
  );
}

function ReportDashboard({ data }) {
  const navigate = useNavigate();
  const { summary, users } = data;

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div
      data-testid="report-dashboard"
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}
    >
      <div className="max-w-5xl mx-auto px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100">TaskPriority Report</h1>
            <p className="text-sm text-slate-400 mt-1">Generated {formatDate(summary.generated_at)}</p>
          </div>
          <button
            data-testid="report-back-to-app-btn"
            onClick={() => navigate("/")}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-2"
            style={{ background: "rgba(30, 41, 59, 0.8)", border: "1px solid rgba(148, 163, 184, 0.15)" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <SummaryCard testId="summary-total-users" icon={<Users className="w-5 h-5 text-blue-400" />} label="Total Users" value={summary.total_users} color="blue" />
          <SummaryCard testId="summary-total-tasks" icon={<ListTodo className="w-5 h-5 text-amber-400" />} label="Total Tasks" value={summary.total_tasks} color="amber" />
          <SummaryCard testId="summary-active-tasks" icon={<Clock className="w-5 h-5 text-emerald-400" />} label="Active" value={summary.active_tasks} color="emerald" />
          <SummaryCard testId="summary-completed-tasks" icon={<CheckSquare className="w-5 h-5 text-violet-400" />} label="Completed" value={summary.completed_tasks} color="violet" />
        </div>

        <div
          className="rounded-xl overflow-hidden"
          style={{ background: "rgba(30, 41, 59, 0.8)", border: "1px solid rgba(148, 163, 184, 0.1)" }}
        >
          <div className="px-6 py-4" style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
            <h2 className="text-base font-medium text-slate-200">User Activity</h2>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="report-users-table" className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148, 163, 184, 0.1)" }}>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-3">User</th>
                  <th className="text-center text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Total</th>
                  <th className="text-center text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Active</th>
                  <th className="text-center text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Done</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Last Active</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => (
                  <UserRow key={user.email} user={user} isLast={i === users.length - 1} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Report() {
  const [reportData, setReportData] = useState(null);

  if (!reportData) {
    return <ReportLogin onAuthenticated={setReportData} />;
  }

  return <ReportDashboard data={reportData} />;
}
